import React from 'react';
import { LabelSelector, LargeLabelSelector } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';

const SHARED_ROWS = [
  ['children', 'node', '—', 'Value or label text'],
  ['selected', 'bool', 'false', 'Selected state — success-green border'],
  ['disabled', 'bool', 'false', 'Disables the option'],
  ['favourite', 'bool', 'false', 'Heart badge in the top-right corner'],
  ['showValue', 'bool', 'true', 'Show the text value'],
  ['showBooleans', 'bool', 'false', 'Show Check / X icons instead of, or alongside, the value'],
  ['showYes', 'bool', 'true', 'Show the Check icon when showBooleans is set'],
  ['showNo', 'bool', 'true', 'Show the X icon when showBooleans is set'],
  ['onClick', 'func', '—', 'Click handler'],
  ['className', 'string', "''", 'Additional CSS class'],
];

export const meta = {
  component: LabelSelector,
  componentName: 'LabelSelector',
  title: 'Label Selector',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '3812:1131',
  description:
    'Fixed 48×40px cell for short values — sizes, quantities, yes/no. For longer option text use LargeLabelSelector, which is the same component with content-driven width; never widen this one with a page-level override.',
  propSets: [
    { title: 'LabelSelector', component: LabelSelector, componentName: 'LabelSelector', rows: SHARED_ROWS },
    { title: 'LargeLabelSelector', component: LargeLabelSelector, componentName: 'LargeLabelSelector', rows: SHARED_ROWS },
  ],
  propsNote:
    'LargeLabelSelector wraps LabelSelector and takes exactly the same props — only the horizontal padding and the auto width differ.',
  usage: [
    {
      title: 'Size picker',
      code: `import { LabelSelector } from '@ajat/design-system/Configurator';

{['XS', 'S', 'M', 'L', 'XL'].map((size) => (
  <LabelSelector
    key={size}
    selected={size === selectedSize}
    disabled={soldOut.includes(size)}
    onClick={() => setSize(size)}
  >
    {size}
  </LabelSelector>
))}`,
    },
    {
      title: 'Long option labels',
      code: `import { LargeLabelSelector } from '@ajat/design-system/Configurator';

// Content-width — the right choice for anything longer than
// a couple of characters.
<LargeLabelSelector selected>Ja, skolens</LargeLabelSelector>
<LargeLabelSelector>Ja, vores eget design</LargeLabelSelector>`,
    },
  ],
};

export default function LabelSelectorPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="value mode">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><LabelSelector>1</LabelSelector></Demo>
          <Demo caption="SELECTED" center><LabelSelector selected>1</LabelSelector></Demo>
          <Demo caption="DISABLED" center><LabelSelector disabled>1</LabelSelector></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Favourite badge" hint="heart in the top-right corner">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><LabelSelector favourite>1</LabelSelector></Demo>
          <Demo caption="SELECTED" center><LabelSelector favourite selected>1</LabelSelector></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Boolean mode" hint="Check / X instead of a value">
        <DemoRow align="center">
          <Demo caption="BOTH" center><LabelSelector showValue={false} showBooleans>1</LabelSelector></Demo>
          <Demo caption="YES ONLY" center><LabelSelector showValue={false} showBooleans showNo={false}>1</LabelSelector></Demo>
          <Demo caption="NO ONLY" center><LabelSelector showValue={false} showBooleans showYes={false}>1</LabelSelector></Demo>
          <Demo caption="DISABLED" center><LabelSelector showValue={false} showBooleans disabled>1</LabelSelector></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Value + booleans">
        <DemoRow align="center">
          <LabelSelector showBooleans favourite>1</LabelSelector>
          <LabelSelector showBooleans selected favourite>1</LabelSelector>
          <LabelSelector showBooleans disabled>1</LabelSelector>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Size picker row">
        <div className="flex items-center gap-[var(--space-ui-small-m)]">
          {['XS', 'S', 'M', 'L', 'XL'].map((size) => (
            <LabelSelector key={size} selected={size === 'M'} disabled={size === 'XS'} favourite={size === 'L'}>
              {size}
            </LabelSelector>
          ))}
        </div>
      </DemoCard>

      <DemoCard title="LargeLabelSelector" hint="width follows the content, height stays 40px">
        <div className="flex flex-wrap items-center gap-[var(--space-ui-small-l)]">
          <LargeLabelSelector>Ja, skolens</LargeLabelSelector>
          <LargeLabelSelector selected>Ja, vores eget design</LargeLabelSelector>
          <LargeLabelSelector disabled>Sort tryk (lyse trøjer)</LargeLabelSelector>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
